/**
 * "Body" of an IF — the statements that follow `IF <premise>` on subsequent
 * lines. Per the DATA(Q) Format Guide, an IF can be followed by:
 *   TERMINATE | SKIP TO <label> | BEGIN/END block | a question.
 *
 * We model the simple shapes structurally; "question" is a one-shot stub
 * (label + text + type) the user customises in the editor afterwards.
 */
import { quoteLabel } from "./grammar"

export type BodyQuestionType =
  | "SELECT ONE"
  | "SELECT MULTIPLE"
  | "FILL IN NUMBER"
  | "FILL IN REAL"
  | "FILL IN TEXT"

export const BODY_QUESTION_TYPES: BodyQuestionType[] = [
  "SELECT ONE",
  "SELECT MULTIPLE",
  "FILL IN NUMBER",
  "FILL IN REAL",
  "FILL IN TEXT",
]

export type BodyAction =
  | { kind: "none" }
  | { kind: "terminate" }
  | { kind: "skip-to"; targetLabel: string }
  | {
      kind: "question"
      label: string
      text: string
      type: BodyQuestionType
    }
  | { kind: "begin-end"; innerText: string }

const TERMINATE_RE = /^\s*TERMINATE\s*$/i
const SKIP_TO_RE = /^\s*SKIP\s+TO\s+(.+?)\s*$/i
const BEGIN_RE = /^\s*BEGIN\s*$/i
const END_RE = /^\s*END\s*$/i

/**
 * Parse the lines that follow an IF.
 *
 * Returns:
 *   - `action`: the structured body, or `{ kind: "none" }` if nothing
 *     identifiable was found.
 *   - `consumed`: how many lines (counting from the input) belong to this
 *     body. Caller uses this to extend the replacement range.
 *
 * Anything we don't recognise (most importantly a `Q LABEL:` question, which
 * could be many lines and is risky to consume) returns `consumed = 0` so the
 * wizard's range stays just the IF line and existing content below is left
 * untouched.
 */
export function parseBodyLines(lines: string[]): {
  action: BodyAction
  consumed: number
} {
  let i = 0
  while (i < lines.length && lines[i].trim() === "") i++
  if (i >= lines.length) return { action: { kind: "none" }, consumed: 0 }

  const first = lines[i]

  if (TERMINATE_RE.test(first)) {
    return { action: { kind: "terminate" }, consumed: i + 1 }
  }

  const skipMatch = first.match(SKIP_TO_RE)
  if (skipMatch) {
    const target = skipMatch[1].trim().replace(/^<<|>>$/g, "")
    return {
      action: { kind: "skip-to", targetLabel: target },
      consumed: i + 1,
    }
  }

  if (BEGIN_RE.test(first)) {
    const innerLines: string[] = []
    let depth = 1
    let j = i + 1
    while (j < lines.length) {
      if (BEGIN_RE.test(lines[j])) {
        depth++
        innerLines.push(lines[j])
      } else if (END_RE.test(lines[j])) {
        depth--
        if (depth === 0) {
          return {
            action: { kind: "begin-end", innerText: innerLines.join("\n") },
            consumed: j + 1,
          }
        }
        innerLines.push(lines[j])
      } else {
        innerLines.push(lines[j])
      }
      j++
    }
    // Unmatched BEGIN — don't consume; leave as-is.
    return { action: { kind: "none" }, consumed: 0 }
  }

  // Anything else (questions, notes, sections, raw text) — leave alone.
  return { action: { kind: "none" }, consumed: 0 }
}

/**
 * Emit the body as one or more lines. Returns `[]` for `none`.
 */
export function generateBodyLines(action: BodyAction): string[] {
  switch (action.kind) {
    case "none":
      return []
    case "terminate":
      return ["TERMINATE"]
    case "skip-to":
      return [`SKIP TO ${quoteLabel(action.targetLabel)}`]
    case "question": {
      const out = [`Q ${action.label}: ${action.text}`]
      if (action.type.startsWith("FILL IN")) {
        out.push(action.type)
      } else {
        out.push(`${action.type} FROM`)
        out.push("# Add your answer options here")
      }
      return out
    }
    case "begin-end": {
      const out = ["BEGIN"]
      const inner = action.innerText.trim()
      if (inner.length > 0) {
        out.push(...inner.split("\n"))
      } else {
        out.push("# Add your statements here")
      }
      out.push("END")
      return out
    }
  }
}

export function isBodyComplete(action: BodyAction): boolean {
  switch (action.kind) {
    case "none":
    case "terminate":
    case "begin-end":
      return true
    case "skip-to":
      return action.targetLabel.trim() !== ""
    case "question":
      return action.label.trim() !== "" && action.text.trim() !== ""
  }
}

export const DEFAULT_BODY_ACTION: BodyAction = { kind: "none" }
